import { ClickAwayListener, Popper } from '@material-ui/core';
import clsx from 'clsx';
import { debounce } from 'lodash';
import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';

import Button from '@/src/components/common/Elements/Button/Button';
import Checkbox from '@/src/components/common/Elements/Checkbox/Checkbox';
import Icon, { ICONS } from '@/src/components/common/Elements/Icon/Icon';
import InputText from '@/src/components/common/Elements/InputText/InputText';
import { useToggle } from '@/src/hooks/useToggle';

import { TagPill } from '../TagPill/TagPill';

export type DocumentTagFilterOption = {
  id: number;
  value: number;
  label: string;
  color: string;
  isChecked: boolean;
};

const TagsFilterButton = ({
  buttonLabel,
  filterOptions,
  filterPopupWidth,
  count = 0,
  handleChangeCheckboxFilter,
  handleClickAllChecked,
  hasSearch = false,
  searchPlaceholder = 'Search',
  noResultsLabel = 'No Result Found',
}: {
  buttonLabel: string;
  filterOptions: DocumentTagFilterOption[];
  filterPopupWidth: string;
  handleChangeCheckboxFilter: (value: any) => void;
  handleClickAllChecked: (isAllChecked: boolean) => void;
  count?: number;
  hasSearch?: boolean;
  searchPlaceholder?: string;
  noResultsLabel?: string;
}) => {
  const ref = useRef(null);
  const [isPopperOpen, togglePopperOpen] = useToggle(false);
  const [query, setQuery] = useState(''); // Search state

  const handleSearchChange = useMemo(
    () =>
      debounce((val: string) => {
        setQuery(val.toLowerCase());
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      handleSearchChange.cancel();
    };
  }, [handleSearchChange]);

  const isAllChecked = useMemo(() => {
    return filterOptions.filter((filterOption) => !filterOption.isChecked).length === 0;
  }, [filterOptions]);

  // Filter options based on query
  const filteredOptions = useMemo(() => {
    if (!query) return filterOptions;
    return filterOptions.filter((opt) =>
      typeof opt.label === 'string' ? opt.label.toLowerCase().includes(query) : false
    );
  }, [filterOptions, query]);

  const handleClose = useCallback(() => {
    togglePopperOpen(false);
    setQuery('');
  }, [togglePopperOpen]);

  return (
    <>
      <Button
        onClick={() => togglePopperOpen()}
        buttonRef={ref}
        buttonClassSettings={{
          variant: 'unstyled',
          isActive: isPopperOpen || count > 0,
        }}
        className="tw-group tw-relative tw-gap-[5px] tw-m-0 tw-px-[10px] tw-py-[6px] tw-text-[14px] tw-font-brandonTextMedium tw-border-none"
      >
        <span>{buttonLabel}</span>
        <Icon
          icon={count > 0 ? ICONS.funnel() : ICONS.filter_list}
          className={clsx(
            'material-icons tw-text-[18px] tw-text-cfo-blue-600 group-hover:tw-visible',
            count > 0 ? 'tw-visible' : 'tw-invisible'
          )}
        />
        {count > 0 && (
          <span
            className="count-container tw-text-[8px] tw-rounded-full tw-bg-cfo-blue-600 tw-text-white tw-absolute"
            style={{
              height: '12px',
              minWidth: '12px',
              lineHeight: '1.5',
              right: '7px',
              top: '2px',
            }}
          >
            {count}
          </span>
        )}
      </Button>
      <Popper open={isPopperOpen} anchorEl={ref.current} placement="bottom" className="tw-z-10">
        <ClickAwayListener onClickAway={() => handleClose()}>
          <div
            className="tw-overflow-auto tw-bg-white tw-border tw-border-cfo-grey-500"
            style={{
              maxHeight: '380px',
              width: filterPopupWidth,
              borderRadius: '0 0 4px',
              boxShadow: '3px 4px 5px 0px #0000000F',
            }}
          >
            <ul className="tw-flex tw-flex-col tw-divide-y tw-divide-cfo-grey-500 !tw-m-0 tw-p-0 tw-list-none">
              <li className="tw-p-[10px] tw-text-cfo-grey-600 ">
                <span className="">Filter by</span>
              </li>
              {hasSearch && (
                <li>
                  <InputText
                    containerClassName="!tw-rounded-none"
                    startIcon={
                      <Icon icon={ICONS.search} className="tw-text-[18px] tw-text-cfo-grey-600" />
                    }
                    placeholder={searchPlaceholder}
                    onInput={(e) => handleSearchChange(e.target.value)}
                    value={null}
                    name="tags-query"
                  />
                </li>
              )}
              <li className="tw-p-[10px] tw-flex tw-gap-[5px] tw-items-center hover:tw-font-brandonTextMedium">
                <Checkbox
                  name="task-filter-all"
                  checkboxSize={'13.5px'}
                  value="all"
                  checked={isAllChecked}
                  onChange={() => handleClickAllChecked(isAllChecked)}
                />
                <span className="tw-font-brandonTextMedium">All Tags</span>
              </li>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((filterOption) => {
                  if (filterOption.value === 0) {
                    return (
                      <li
                        className="tw-p-[10px] tw-flex tw-gap-[5px] tw-items-center hover:tw-font-brandonTextMedium"
                        key={filterOption.value}
                      >
                        <Checkbox
                          name="task-filter-blank"
                          checkboxSize={'13.5px'}
                          value={filterOption.value}
                          checked={filterOption.isChecked}
                          onChange={handleChangeCheckboxFilter}
                        />
                        <span className="tw-font-brandonTextMedium">{filterOption.label}</span>
                      </li>
                    );
                  }

                  return (
                    <li
                      className="tw-p-[10px] tw-flex tw-gap-[5px] tw-items-center hover:tw-font-brandonTextMedium"
                      key={filterOption.value}
                    >
                      <Checkbox
                        name="task-filter-status"
                        checkboxSize={'13.5px'}
                        value={filterOption.value}
                        onChange={handleChangeCheckboxFilter}
                        checked={filterOption.isChecked}
                      />
                      <TagPill tag={filterOption} />
                    </li>
                  );
                })
              ) : (
                <li className="tw-p-[10px] tw-text-cfo-grey-500">{noResultsLabel}</li>
              )}
            </ul>
          </div>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default TagsFilterButton;
